'use client';

import { useId, useState } from 'react';

import { CloseIcon } from '@/components/primitives/icons/CloseIcon';
import { InfoIcon } from '@/components/primitives/icons/InfoIcon';
import { TrendArrowIcon } from '@/components/primitives/icons/TrendArrowIcon';
import {
  RED_LIST_INDEX_DETERIORATED_COLOR,
  RED_LIST_INDEX_IMPROVED_COLOR,
} from '@/constants/stateCharts';

import styles from './RedListIndexMobileInfo.module.css';

import type { RedListIndexStats } from '@/content/data/charts/red-list-index';

type RedListIndexMobileInfoProps = {
  stats: RedListIndexStats;
  improvedCaption: string;
  deterioratedCaption: string;
  openLabel: string;
  closeLabel: string;
  dialogLabel: string;
};

/**
 * Compact (below `lg`) info control for Red List Index — (i) toggles a panel
 * with improved / deteriorated migratory-species counts (Figma mobile RLI).
 */
export function RedListIndexMobileInfo({
  stats,
  improvedCaption,
  deterioratedCaption,
  openLabel,
  closeLabel,
  dialogLabel,
}: RedListIndexMobileInfoProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.infoButton}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={open ? closeLabel : openLabel}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {open ? <CloseIcon size={20} /> : <InfoIcon size={20} />}
      </button>

      {open ? (
        <div
          id={panelId}
          className={styles.panel}
          role="dialog"
          aria-label={dialogLabel}
        >
          <div className={styles.stat}>
            <p
              className={styles.value}
              style={{ color: RED_LIST_INDEX_IMPROVED_COLOR }}
            >
              <span>{stats.improvedCount}</span>
              <span className={styles.arrow} aria-hidden>
                <TrendArrowIcon direction="up" />
              </span>
            </p>
            <p className={styles.caption}>{improvedCaption}</p>
          </div>

          <div className={styles.stat}>
            <p
              className={styles.value}
              style={{ color: RED_LIST_INDEX_DETERIORATED_COLOR }}
            >
              <span>{stats.deterioratedCount}</span>
              <span className={styles.arrow} aria-hidden>
                <TrendArrowIcon direction="down" />
              </span>
            </p>
            <p className={styles.caption}>{deterioratedCaption}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
